import React, { useEffect, useState, useRef, useCallback } from 'react';
import StatusIndicator from './StatusIndicator';
import ConnectionCount from './ConnectionCount';
import ActionButton from './ActionButton';
import SSHTerminal from './SSHTerminal';
import VNCViewer from './VNCViewer';
import VNCProxy from './VNCProxy';
import KVMView from './KVMView';
import SunshineView from './SunshineView';
import SpeedTestResult from './SpeedTestResult';
import { SimpleEditableVesselName } from './EditableVesselName';
import { getCsrfToken } from '../utils/auth';
import { useUser } from '../contexts/UserContext';
import { updateDevkit } from '../utils/updateDevkit';
import { supportsModelUpload, supportsSpeedTest } from '../utils/poseidon';
import { formatLicenseExpiration } from '../utils/license';

const MODEL_UPLOAD_BATCH_LIMIT = 10;
import ConnectionIcons from './ConnectionIcons';
import PeripheralIcons from './PeripheralIcons';
import { buildPeripherals, lastKnownPayload } from '../utils/peripherals';
import { getConnections } from '../utils/connections';
import CopyButton from './CopyButton';
import bellOn from '../assets/bellOn.svg';
import bellOff from '../assets/bellOff.svg';
import download from '../assets/download.svg';
import appleIconWhite from '../assets/appleIconWhite.svg';

const DevkitCard = ({ devkit, setDevices, companyList, isPinned = false, onTogglePin, onSessionOpenChange, connections }) => {
	const [terminalOpen, setTerminalOpen] = useState(false);
	const [sshHost, setSshHost] = useState(null);
	const [vncMode, setVncMode] = useState(null);
	const [showVncSelector, setShowVncSelector] = useState(false);
	const [streamOpen, setStreamOpen] = useState(false);
	// Whether Sunshine is installed comes from Back4App (devkit.sunshine_installed),
	// so no per-device probing is needed. Install is done from the Sunshine
	// Streaming menu, not here.
	const sunshineInstalled = devkit.sunshine_installed === true;
	// const admin = localStorage.getItem('admin') === 'true';
	const { admin } = useUser()

	// Mac Minis don't run the devkit services (Poseidon/updates/license), so
	// this card skips those fetches and instead probes SSH/VNC reachability
	// directly to decide whether the remote-access buttons are enabled.
	const isMacMini = devkit.is_mac_mini === true;
	const [macMiniOnline, setMacMiniOnline] = useState(devkit.status === 'online');

	// Devkits with a JetKVM attached, keyed by devkit_id → KVM tailscale IP
	const KVM_HOSTS = { '1002': '100.72.57.119' };
	const kvmHost = isMacMini ? KVM_HOSTS[String(devkit.devkit_id)] : undefined;
	const [kvmOpen, setKvmOpen] = useState(false);
	const [kvmOnline, setKvmOnline] = useState(false);

	// Tell the grid while any remote session (SSH/VNC/KVM/stream) is open:
	// the sessions live inside this card, so the grid must keep the card
	// mounted even when filters ("only show online", company, search) would
	// hide it — unmounting closes the session's window out from under the user.
	const hasOpenRemoteSession = Boolean(terminalOpen || vncMode || kvmOpen || streamOpen);
	useEffect(() => {
		if (!onSessionOpenChange || !hasOpenRemoteSession) return undefined;
		onSessionOpenChange(devkit.devkit_id, true);
		return () => onSessionOpenChange(devkit.devkit_id, false);
	}, [hasOpenRemoteSession, devkit.devkit_id, onSessionOpenChange]);

	useEffect(() => {
		if (!kvmHost) return undefined;

		let cancelled = false;

		const checkKvmStatus = async () => {
			try {
				const res = await fetch(`/api/host-reachability?host=${kvmHost}&port=80`, {
					method: 'GET',
					credentials: 'include',
				});
				const data = res.ok ? await res.json().catch(() => ({})) : {};
				if (!cancelled) setKvmOnline(Boolean(data?.reachable));
			} catch {
				if (!cancelled) setKvmOnline(false);
			}
		};

		checkKvmStatus();
		const id = setInterval(checkKvmStatus, 30000);
		return () => { cancelled = true; clearInterval(id); };
	}, [kvmHost]);

	useEffect(() => {
		if (!isMacMini) return undefined;
		const ip = devkit.tailscale?.ipv4?.[0];
		if (!ip) return undefined;

		let cancelled = false;

		const checkStatus = async () => {
			try {
				const checks = await Promise.all(
					[22, 5900].map(async (port) => {
						const res = await fetch(`/api/host-reachability?host=${ip}&port=${port}`, {
							method: 'GET',
							credentials: 'include',
						});
						if (!res.ok) return false;
						const data = await res.json().catch(() => ({}));
						return Boolean(data?.reachable);
					})
				);
				if (!cancelled) setMacMiniOnline(checks.some(Boolean));
			} catch {
				if (!cancelled) setMacMiniOnline(false);
			}
		};

		checkStatus();
		const id = setInterval(checkStatus, 30000);
		return () => { cancelled = true; clearInterval(id); };
	}, [isMacMini, devkit.tailscale?.ipv4?.[0]]);

	const hasFetchedInitialChannels = useRef(false);

	const [vesselName, setVesselName] = useState(devkit.vessel_name || '');
	const [isSavingVessel, setIsSavingVessel] = useState(false);

	const [updatingProteusCore, setUpdatingProteusCore] = useState(false);
	const [updatingApolloCore, setUpdatingApolloCore] = useState(false);
	const [updatingIronman, setUpdatingIronman] = useState(false);

	const [refreshDevkit, setRefreshDevkit] = useState(false);
	// { ok: boolean, message?: string } | null — result popup for the Poseidon update
	const [confirmationPopup, setConfirmationPopup] = useState(null);

	const [proteusCoreHasUpdates, setProteusCoreHasUpdates] = useState(false);
	const [apolloCoreHasUpdates, setApolloCoreHasUpdates] = useState(false);
	const [ironmanHasUpdates, setIronmanHasUpdates] = useState(false);

	const [proteusCoreUpdateChannel, setProteusCoreUpdateChannel] = useState('');
	const [apolloCoreUpdateChannel, setApolloCoreUpdateChannel] = useState('');
	const [ironmanUpdateChannel, setIronmanUpdateChannel] = useState('');

	const hasApolloCore = Number(devkit.devkit_id) >= 2000;
	const poseidonVersion =
		devkit?.versions?.poseidon?.poseidon_version ??
		devkit?.last_poseidon_version ??
		'';
	const canUploadModel = supportsModelUpload(poseidonVersion);
	const canRunSpeedTest = supportsSpeedTest(poseidonVersion);

	const apolloCoreVersion =
		devkit?.versions?.apollocore?.apollocore_version ??
		devkit?.last_apollocore_version ??
		'unknown';
	const apolloCoreBuild =
		devkit?.versions?.apollocore?.apollocore_build_number ??
		devkit?.last_apollocore_build ??
		'unknown';
	const proteusCoreVersion =
		devkit?.versions?.proteuscore?.proteuscore_version ??
		devkit?.last_proteuscore_version ??
		'unknown';
	const proteusCoreBuild =
		devkit?.versions?.proteuscore?.proteuscore_build_number ??
		devkit?.last_proteuscore_build ??
		'unknown';
	const ironmanVersion =
		devkit?.versions?.ironman?.ironman_version ??
		devkit?.last_ironman_version ??
		'unknown';
	const ironmanBuild =
		devkit?.versions?.ironman?.ironman_build_number ??
		devkit?.last_ironman_build ??
		'unknown';

	const [confirmPoseidon, setConfirmPoseidon] = useState(false);
	const [isSendingPoseidon, setIsSendingPoseidon] = useState(false);

	const [adminInfoOpen, setAdminInfoOpen] = useState(false);
	const [busyUpdateApps, setBusyUpdateApps] = useState(() => new Set());
	const handleUpdateActiveChange = useCallback((app) => (isActive) => {
		setBusyUpdateApps((prev) => {
			const next = new Set(prev);
			if (isActive) next.add(app); else next.delete(app);
			return next;
		});
	}, []);
	// Which app's update prompt is open ('apollocore' | 'proteuscore' | 'ironman' | null).
	// busyUpdateApps (fed by onSendingChange) blocks closing the prompt mid-update.
	const [updateModalApp, setUpdateModalApp] = useState(null);

	const updateSelectorSetters = {
		apollocore: setUpdatingApolloCore,
		proteuscore: setUpdatingProteusCore,
		ironman: setUpdatingIronman,
	};

	const openUpdateModal = (appKey) => {
		if (appKey === 'poseidon') {
			setConfirmPoseidon(true);
			return;
		}
		updateSelectorSetters[appKey]?.(true);
		setUpdateModalApp(appKey);
	};

	const closeUpdateModal = () => {
		if (busyUpdateApps.has(updateModalApp)) return;
		updateSelectorSetters[updateModalApp]?.(false);
		setUpdateModalApp(null);
	};

	const [editingCompany, setEditingCompany] = useState(false);
	const [selectedCompany, setSelectedCompany] = useState(devkit.company || '');
	const [addingNew, setAddingNew] = useState(false);
	const [newCompanyName, setNewCompanyName] = useState('');

	const [licenseKey, setLicenseKey] = useState('');
	const [isUpdatingLicense, setIsUpdatingLicense] = useState(false);
	const [licenseMsg, setLicenseMsg] = useState(null);
	const licenseInputRef = useRef(null);

	const [downloading, setDownloading] = useState(false);
	const [downloaded, setDownloaded] = useState(false);

	const [uploadingModel, setUploadingModel] = useState(false);
	const [modelUploadMsg, setModelUploadMsg] = useState(null);
	const [modelsList, setModelsList] = useState(null);
	const [tbPackages, setTbPackages] = useState(null);
	const [modelUploadProgress, setModelUploadProgress] = useState(null);
	const modelFileInputRef = useRef(null);

	const [runningSpeedTest, setRunningSpeedTest] = useState(false);
	const [speedTestResult, setSpeedTestResult] = useState(null);
	const [speedTestError, setSpeedTestError] = useState(null);

	const [watching, setWatching] = useState(Boolean(devkit.notifying) || false);
	const [savingWatch, setSavingWatch] = useState(false);

	const openSSH = (host) => {
		setSshHost(host);
		setTerminalOpen(true);
	};
	const openVncSelector = () => {
		if (false) { // Disable direct vnc for now
			setShowVncSelector(true);
		} else {
			handleVncChoice('proxy')
		}
	}

	const handleVncChoice = (mode) => {
		setShowVncSelector(false);
		setVncMode(mode);
	};

	// const closeVnc = () => setVncMode(null);
	const closeVnc = useCallback(() => setVncMode(null), [setVncMode]);

	const handleUpdateVersions = async (data) => {
		try {
			const res = await fetch(`/api/update-last-known-versions`, {
				method: 'POST',
				credentials: 'include', // send JWT cookies
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-TOKEN': getCsrfToken(),
				},
				body: JSON.stringify({
					devkit_id: devkit.devkit_id,
					proteuscore_version: data.versions.proteuscore.proteuscore_version,
					proteuscore_build: Number(data.versions.proteuscore.proteuscore_build_number),
					apollocore_version: data.versions.apollocore?.apollocore_version ?? null,
					// Devkits.last_apollocore_build is a String column in Parse,
					// unlike the other *_build columns which are Numbers.
					apollocore_build: data.versions.apollocore?.apollocore_build_number != null
						? String(data.versions.apollocore.apollocore_build_number)
						: null,
					ironman_version: data.versions.ironman.ironman_version,
					ironman_build: Number(data.versions.ironman.ironman_build_number),
					poseidon_version: data.versions.poseidon.poseidon_version,
					image_version: data.versions.poseidon.devkit_image_version
				}),
			});

			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.error || 'Update failed');
			}
			const updateResult = await res.json();
		} catch (error) {
			console.error('Failed to save versions:', error);
		}
	};

	const getDeviceVersions = async () => {
		if (devkit.status === 'offline') {
			return
		}

		const res = await fetch(
			`/api/versions/${devkit.tailscale.ipv4[0]}`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					'X-CSRF-TOKEN': getCsrfToken()
				}
			}
		);
		if (!res.ok) return;
		const data = await res.json();

		setDevices((prev) =>
			!prev.length
				? prev
				: prev.map((d) =>
					d.devkit_id === devkit.devkit_id
						? { ...d, versions: data.versions }
						: d
				)
		);

		handleUpdateVersions(data)
	};

	const performPoseidonUpdate = async () => {
		setIsSendingPoseidon(true);
		try {
			await updateDevkit({
				host: devkit.tailscale.ipv4[0],
				app: 'poseidon',
				updateBody: { version: 'latest' },
				devkitName: "Poseidon",
			})
			setConfirmationPopup({ ok: true });
		} catch (err) {
			console.error('Poseidon update failed:', err);
			setConfirmationPopup({ ok: false, message: err?.message || 'Update failed' });
		} finally {
			// Poseidon is down while it restarts, so re-check versions a few times.
			[15_000, 45_000, 90_000].forEach((ms) => setTimeout(() => setRefreshDevkit((p) => !p), ms));
			setIsSendingPoseidon(false);
		}
	};

	const checkForUpdates = async () => {
		if (devkit.status === 'offline') return;
		try {
			const res = await fetch(
				`/api/initial-release-channels/${devkit.tailscale.ipv4[0]}`,
				{
					method: 'GET',
					credentials: 'include'
				}
			);

			if (!res.ok) {
				return;
			}

			let data;
			try {
				data = await res.json();
			} catch (jsonErr) {
				return;
			}

			const newCoreChannel = data?.release_channels?.proteuscore ?? '';
			const newApolloCoreChannel = data?.release_channels?.apollocore ?? '';
			const newIronmanChannel = data?.release_channels?.ironman ?? '';

			if (!hasFetchedInitialChannels.current) {
				setProteusCoreUpdateChannel(newCoreChannel);
				setApolloCoreUpdateChannel(newApolloCoreChannel);
				setIronmanUpdateChannel(newIronmanChannel);
				hasFetchedInitialChannels.current = true;
			}
		} catch (err) {
			console.error('checkForUpdates error:', err);
		}
	};

	const getLicense = async () => {
				if (devkit.status === 'offline') {
			return
		}

		const res = await fetch(
			`/api/license/${devkit.tailscale.ipv4[0]}`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					'X-CSRF-TOKEN': getCsrfToken()
				}
			}
		);
		if (!res.ok) return;
		const data = await res.json();

		const proteusLicense = data?.proteuscore?.license ?? null;
		const proteusExpiration = data?.proteuscore?.expiration_date ?? null;
		const apolloLicense = data?.apollocore?.license ?? null;
		const apolloExpiration = data?.apollocore?.expiration_date ?? null;

		setDevices((prev) =>
			!prev.length
				? prev
				: prev.map((d) =>
					d.devkit_id === devkit.devkit_id
						? {
							...d,
							last_license: proteusLicense ?? d.last_license,
							proteuscore_license: proteusLicense,
							proteuscore_license_expiration: proteusExpiration,
							proteuscore_license_days_remaining: data?.proteuscore?.days_remaining ?? null,
							apollocore_license: apolloLicense,
							apollocore_license_expiration: apolloExpiration,
							apollocore_license_days_remaining: data?.apollocore?.days_remaining ?? null,
						}
						: d
				)
		);

		// Persist to Back4App only when something we store has actually changed
		const changed =
			(proteusLicense && proteusLicense !== devkit.last_license) ||
			(proteusExpiration && proteusExpiration !== devkit.last_proteuscore_license_expiration) ||
			(apolloLicense && apolloLicense !== devkit.last_apollocore_license) ||
			(apolloExpiration && apolloExpiration !== devkit.last_apollocore_license_expiration);

		if (changed) {
			handleUpdateLastKnownLicense({
				license: proteusLicense,
				apollocore_license: apolloLicense,
				proteuscore_license_expiration: proteusExpiration,
				apollocore_license_expiration: apolloExpiration,
			});
		}
	}

	const handleUpdateLastKnownLicense = async (licenseFields) => {
				try {
			const res = await fetch(`/api/update-last-known-license`, {
				method: 'POST',
				credentials: 'include', // send JWT cookies
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-TOKEN': getCsrfToken(),
				},
				body: JSON.stringify({
					devkit_id: devkit.devkit_id,
					...licenseFields,
				}),
			});

			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.error || 'Update failed');
			}
			const updateResult = await res.json();
			// console.log('updateResult:', updateResult);
		} catch (error) {
			console.error('Failed to save versions:', error);
		}
	}

	// Radar / camera / sensor inventory. Kept in card state (not on the shared
	// device object) because Dashboard's 30s list poll only preserves `versions`.
	const [peripherals, setPeripherals] = useState(null);
	const [peripheralsLoading, setPeripheralsLoading] = useState(false);

	const getPeripherals = async () => {
		if (devkit.status === 'offline') {
			setPeripherals(null);
			return;
		}
		setPeripheralsLoading(true);
		try {
			const res = await fetch(`/api/peripherals/${devkit.tailscale.ipv4[0]}`, {
				credentials: 'include',
				headers: { 'X-CSRF-TOKEN': getCsrfToken() },
			});
			if (!res.ok) { setPeripherals(null); return; }
			const data = await res.json();
			setPeripherals(data);

			// Persist last-known model / seen so offline cards can still say
			// "Simrad Halo, last seen 3 hrs ago".
			if (data.radar || data.camera || data.sensors) {
				fetch('/api/update-last-known-peripherals', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken() },
					body: JSON.stringify(lastKnownPayload(data, devkit.devkit_id)),
				}).catch((e) => console.error('Failed to save peripherals:', e));
			}
		} catch (e) {
			console.error('Failed to fetch peripherals:', e);
			setPeripherals(null);
		} finally {
			setPeripheralsLoading(false);
		}
	};

	useEffect(() => {
		if (isMacMini) return;
		getDeviceVersions();
		getLicense();
		getPeripherals();
	}, [refreshDevkit, updatingIronman, updatingProteusCore, updatingApolloCore]);
	useEffect(() => {
		if (isMacMini) return;
		checkForUpdates();
	}, [devkit]);

	// Remote-access buttons: Mac Minis go by the live port probe, devkits by
	// the polled Tailscale status.
	const remoteOnline = isMacMini ? macMiniOnline : devkit.status !== 'offline';


	const handleCompanyChange = async (company) => {
		setSelectedCompany(company);
		setEditingCompany(false);
		setAddingNew(false);
		setNewCompanyName('');

		await fetch(`/api/update-company`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
				'X-CSRF-TOKEN': getCsrfToken(),
			},
			body: JSON.stringify({
				devkit_id: devkit.devkit_id,
				new_company: company,
			}),
		});
	};

	const handleSelectChange = (e) => {
		const value = e.target.value;
		if (value === '__add__') {
			setAddingNew(true);
		} else {
			handleCompanyChange(value);
		}
	};

	const handleNewCompanySubmit = async () => {
		if (newCompanyName.trim() === '') return;
		await handleCompanyChange(newCompanyName.trim());
	};

	const handleNewCompanyKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleNewCompanySubmit();
		} else if (e.key === 'Escape') {
			setAddingNew(false);
			setEditingCompany(false);
		}
	}

	const handleUpdateLicense = async () => {
		const trimmed = licenseKey.trim();
		if (!trimmed) return;
		try {
			setIsUpdatingLicense(true);
			setLicenseMsg(null);

			const res = await fetch('/api/update-license', {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-TOKEN': getCsrfToken(),
				},
				body: JSON.stringify({
					new_license: trimmed,
					host: devkit?.tailscale?.ipv4?.[0],
				}),
			});

			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.error || `HTTP ${res.status}`);
			}

			setLicenseMsg('License updated.');
			setLicenseKey('');
			licenseInputRef.current?.blur();
		} catch (e) {
			console.error('License update failed:', e);
			setLicenseMsg('Failed to update license.');
		} finally {
			setIsUpdatingLicense(false);
			setTimeout(() => setLicenseMsg(null), 3000);
		}
	};

	const handleLicenseKeyPress = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleUpdateLicense();
		}
	};

	const toggleWatch = async () => {
		if (savingWatch) return;
		setSavingWatch(true);
		const next = !watching;
		setWatching(next);

		try {
			const res = await fetch('/api/notify', {
				method: 'POST',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-TOKEN': getCsrfToken()
				},
				body: JSON.stringify({ devkit_id: devkit.tailscale.name.slice(0,4), watch: next }),
			});
			if (!res.ok) throw new Error(`notify failed: ${res.status}`);
		} catch (e) {
			console.error(e);
			setWatching(!next); // revert on error
			alert('Could not update notification preference.');
		} finally {
			setSavingWatch(false);
		}
	}

	const downloadLogs = async () => {
		setDownloading(true);
		setDownloaded(false);
		try {
			const res = await fetch('/api/download-logs', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRF-TOKEN': getCsrfToken()
				},
				credentials: 'include',
				body: JSON.stringify({ host: devkit.tailscale.ipv4[0] }),
			});
			if (!res.ok) throw new Error(await res.text());
			const blob = await res.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			const ts = new Date().toISOString()
				.replace(/[-:]/g, '')
				.replace(/\.\d{3}Z$/, 'Z');

			a.download = `logs_${devkit.devkit_id}_${ts}.zip`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(url);

			setDownloaded(true);
			setTimeout(() => setDownloaded(false), 2500);
		} catch (err) {
			console.error('Download failed:', err);
			alert('Failed to download logs');
		} finally {
			setDownloading(false);
		}
	}

	const fetchModels = async () => {
		if (!admin || !canUploadModel || devkit.status === 'offline') return;
		try {
			const res = await fetch(`/api/models/${devkit.tailscale.ipv4[0]}`, {
				credentials: 'include',
				headers: { 'X-CSRF-TOKEN': getCsrfToken() },
			});
			if (!res.ok) return;
			const data = await res.json();
			if (Array.isArray(data?.models)) setModelsList(data.models);
		} catch { /* */ }
	};

	const fetchTbPackages = async () => {
		if (!admin || !canUploadModel || devkit.status === 'offline') return;
		try {
			const res = await fetch(`/api/models/${devkit.tailscale.ipv4[0]}/tbpackage`, {
				credentials: 'include',
				headers: { 'X-CSRF-TOKEN': getCsrfToken() },
			});
			if (!res.ok) { setTbPackages(null); return; }
			const data = await res.json();
			setTbPackages(Array.isArray(data?.packages) ? data.packages : null);
		} catch { setTbPackages(null); }
	};

	const triggerModelUpload = () => modelFileInputRef.current?.click();

	const handleModelFileChange = async (e) => {
		const MAX_BATCH = MODEL_UPLOAD_BATCH_LIMIT;
		const selected = Array.from(e.target.files || []);
		if (selected.length === 0) return;
		e.target.value = '';
		if (selected.length > MAX_BATCH) {
			const proceed = window.confirm(
				`You selected ${selected.length} files, but only ${MAX_BATCH} can be uploaded at a time.\n\nUpload the first ${MAX_BATCH} now? The remaining ${selected.length - MAX_BATCH} will be skipped.`
			);
			if (!proceed) return;
		}
		const files = selected.slice(0, MAX_BATCH);
		const skipped = selected.length - files.length;
		setUploadingModel(true);
		setModelUploadMsg(null);

		// Device endpoint takes one file per request, so upload sequentially.
		const failures = [];
		let successCount = 0;
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			setModelUploadProgress({ current: i + 1, total: files.length, fileName: file.name });
			try {
				const form = new FormData();
				form.append('file', file);
				const res = await fetch(`/api/upload-model/${devkit.tailscale.ipv4[0]}`, {
					method: 'POST',
					credentials: 'include',
					headers: { 'X-CSRF-TOKEN': getCsrfToken() },
					body: form,
				});
				const payload = await res.json().catch(() => ({}));
				if (!res.ok) {
					throw new Error(payload?.error || `Upload failed (${res.status})`);
				}
				successCount += 1;
			} catch (err) {
				failures.push({ name: file.name, reason: err?.message || 'Upload failed' });
			}
		}

		setModelUploadProgress(null);
		const plural = (n) => (n === 1 ? '' : 's');
		const skipNote = skipped > 0
			? ` ${skipped} additional file${plural(skipped)} skipped (limit is ${MAX_BATCH} per upload).`
			: '';
		if (failures.length === 0) {
			setModelUploadMsg({
				kind: skipped > 0 ? 'error' : 'success',
				text: `Uploaded ${successCount} file${plural(successCount)}.${skipNote}`,
			});
		} else if (successCount === 0) {
			setModelUploadMsg({
				kind: 'error',
				text: `Failed to upload ${failures.length} file${plural(failures.length)}: ${failures.map((f) => f.name).join(', ')}.${skipNote}`,
			});
		} else {
			setModelUploadMsg({
				kind: 'error',
				text: `Uploaded ${successCount} of ${files.length}. Failed: ${failures.map((f) => f.name).join(', ')}.${skipNote}`,
			});
		}

		fetchModels();
		fetchTbPackages();
		setUploadingModel(false);
	};

	useEffect(() => {
		if (modelUploadMsg?.kind !== 'success') return undefined;
		const id = setTimeout(() => setModelUploadMsg((p) => (p?.kind === 'success' ? null : p)), 5000);
		return () => clearTimeout(id);
	}, [modelUploadMsg]);

	useEffect(() => {
		fetchModels();
		fetchTbPackages();
	}, [admin, canUploadModel, devkit.status, devkit.tailscale?.ipv4?.[0]]);

	const runSpeedTest = async () => {
		if (!admin || !canRunSpeedTest || devkit.status === 'offline') return;
		setRunningSpeedTest(true);
		setSpeedTestError(null);
		try {
			const res = await fetch(`/api/speed-test/${devkit.tailscale.ipv4[0]}`, {
				credentials: 'include',
				headers: { 'X-CSRF-TOKEN': getCsrfToken() },
			});
			const payload = await res.json().catch(() => ({}));
			if (!res.ok) {
				if (res.status === 429) throw new Error('Rate limited (2 per minute) — wait a bit and retry.');
				if (res.status === 503) throw new Error('Device is busy (update in progress) — try again when it finishes.');
				throw new Error(payload?.error || `Speed test failed (${res.status})`);
			}
			setSpeedTestResult(payload);
		} catch (err) {
			setSpeedTestError(err?.message || 'Speed test failed');
			setSpeedTestResult(null);
		} finally {
			setRunningSpeedTest(false);
		}
	};

	return (
		<>
			<div
				className={`font-sans bg-white rounded-2xl shadow-sm border border-gray-125 hover:shadow-lg transition-all duration-300 flex flex-col h-full`}
			>
				{/* Header Section */}
				<div className={`px-4 py-3 border-b rounded-t-2xl ${isMacMini ? 'bg-gradient-to-r from-[#F5FAFD] to-[#E8F4FA] border-[#D9E6F0]' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-100'}`}>
					<div className='flex items-start justify-between'>
						<div>
							<div className='flex items-center gap-2'>
								<button
									type="button"
									onClick={() => onTogglePin?.(devkit.devkit_id)}
									title={isPinned ? 'Click to unpin' : 'Click to pin'}
									className={`text-lg font-semibold transition-colors ${isPinned ? 'text-blue-600 hover:text-blue-700' : 'text-gray-900 hover:text-blue-600'}`}
								>
									{devkit.tailscale.name.slice(0, 4)}
								</button>
								{isMacMini && (
									<span className='text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700'>Mac Mini</span>
								)}
							</div>
							{admin ? (
								<div className='mt-1'>
									{addingNew ? (
										<input
											type='text'
											value={newCompanyName}
											onChange={(e) => setNewCompanyName(e.target.value)}
											onKeyDown={handleNewCompanyKeyDown}
											onBlur={handleNewCompanySubmit}
											className='text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm px-2 py-1 w-48'
											placeholder='Enter new company'
											autoFocus
										/>
									) : editingCompany ? (
										<select
											value={selectedCompany}
											onChange={handleSelectChange}
											onBlur={() => setEditingCompany(false)}
											className='text-sm text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm px-2 py-1'
											autoFocus
										>
											{companyList.map((company) => (
												<option key={company} value={company}>
													{company}
												</option>
											))}
											<option value="__add__">Add new company...</option>
										</select>
									) : (
										<button
											onClick={() => setEditingCompany(true)}
											className='text-sm text-gray-500 mt-1'
										>
											{selectedCompany || 'No organization'}
										</button>
									)}
								</div>
							) : (
								<p className='text-sm text-gray-500 mt-1'>
									{devkit.company || 'No organization'}
								</p>
							)}
						</div>
						<div className='flex flex-col items-end gap-1.5'>
							<StatusIndicator status={devkit.status} admin={admin} notifying={devkit.notifying} />
							{devkit.tailscale.updateAvailable && (
								<span className='inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full font-medium'>
									<span className='w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse'></span>
									Update Available
								</span>
							)}

							<div className='flex items-center gap-3 mt-1'>
								{!isMacMini && (
									<PeripheralIcons
										items={buildPeripherals({ live: peripherals, devkit, hasCamera: hasApolloCore })}
										loading={peripheralsLoading && !peripherals}
										size='md'
									/>
								)}
								{!isMacMini && getConnections(devkit?.versions).length > 0 && (
									<span className='w-px h-4 bg-gray-300' aria-hidden='true' />
								)}
								<ConnectionIcons versions={devkit?.versions} size='md' />
							</div>
						</div>
					</div>
				</div>

				{/* Main Content */}
				<div className={`p-4 flex flex-col flex-1 ${devkit.status === "offline" ? "opacity-50" : ""
					}`}
				>
					{/* Info Grid */}
					<div className='grid grid-cols-2 gap-x-4 gap-y-3 mb-4'>
						<div className='space-y-3'>
							{admin && (
								<div className='space-y-1'>
									<p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
										Tocaro VPN
									</p>
									<div className='flex items-center gap-1.5'>
										<a
											href={isMacMini
												? `http://${devkit.tailscale.ipv4[0]}:8081/app`
												: `http://${devkit.tailscale.ipv4[0]}/app?devkit=${devkit.tailscale.name.slice(0, 4)}`}
											className='text-sm text-gray-900 font-medium hover:text-blue-600'
											target='_blank'
											rel='noopener noreferrer'
										>
											{devkit.tailscale.ipv4[0]}
										</a>
										<CopyButton text={devkit.tailscale.ipv4[0]} label='Copy Tailscale IP' />
									</div>
								</div>
							)}

							<div className='space-y-1' title="Connect locally via mDNS when both devices are on the same network.  Note: Windows devices may have limited mDNS support.">
								<p className='text-xs font-medium text-gray-500 tracking-wide'>
									LOCAL NET (mDNS)
								</p>
								<a
									href={isMacMini
										? `http://${devkit.tailscale.name}.local/app`
										: `http://${devkit.tailscale.name.slice(0, 4)}.local/app?devkit=${devkit.tailscale.name.slice(0, 4)}`}
									className='text-sm text-gray-900 font-medium hover:text-blue-600'
									target='_blank'
									rel='noopener noreferrer'
								>
									{devkit.tailscale.hostname}.local
								</a>
							</div>
							{!admin && !isMacMini && (
								<div className='space-y-1'>
									<p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
										{hasApolloCore ? 'ApolloCore' : 'ProteusCore'} DevKit Image
									</p>
									<p className='text-sm text-gray-900 font-medium'>
										v{devkit.last_image_version}
									</p>
								</div>
							)}
						</div>

						<div className='space-y-3'>
							<SimpleEditableVesselName
								devkit={devkit}
								setDevices={setDevices}
							/>
							<div className='text-right'>
								<p className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
									Last Seen
								</p>
								<p className='text-sm text-gray-900 font-medium mt-1'>
									{devkit.lastSeen}
								</p>
							</div>
						</div>
					</div>

					{/* Device Information Collapsible */}
					{admin && !isMacMini && (
						<button
							type="button"
							onClick={() => setAdminInfoOpen((v) => !v)}
							aria-expanded={adminInfoOpen}
							className='mb-4 w-full text-sm font-semibold text-gray-900 flex items-center justify-between gap-2'
						>
							<span className='flex items-center gap-2'>
								<span className='w-1 h-4 bg-amber-500 rounded-full'></span>
								Device Admin Information
							</span>
							<span className={`transition-transform inline-block text-xs text-gray-400 ${adminInfoOpen ? 'rotate-90' : ''}`}>▶</span>
						</button>
					)}

					<div className='relative flex-1 flex flex-col'>
						{admin && !isMacMini && adminInfoOpen && (
						<div className='absolute inset-x-0 bottom-0 -top-4 z-20 bg-gray-50 border border-gray-200 rounded-lg shadow-md overflow-y-auto px-4 pt-3 pb-3 space-y-2 animate-in fade-in duration-150 ease-out'>
							<InfoRow
								label={`${hasApolloCore ? 'ApolloCore' : 'ProteusCore'} DevKit Image`}
								value={
									devkit?.versions?.poseidon?.devkit_image_version
										? `v${devkit.versions.poseidon.devkit_image_version}`
										: devkit?.last_image_version !== 'Unknown' && devkit?.last_image_version
											? `v${devkit.last_image_version}`
											: 'Unknown'
								}
							/>
							<InfoRow
								label='ProteusCore Channel'
								value={proteusCoreUpdateChannel || '—'}
							/>
							<InfoRow
								label='Ironman Channel'
								value={ironmanUpdateChannel || '—'}
							/>

							<InfoRow
								label="ProteusCore License"
								value={devkit?.proteuscore_license || devkit?.last_license || '—'}
							/>
							<InfoRow
								label="ProteusCore License Expiration Date"
								value={formatLicenseExpiration(
									devkit?.proteuscore_license_expiration || devkit?.last_proteuscore_license_expiration
								)}
							/>

							{hasApolloCore && (
								<>
									<InfoRow
										label="ApolloCore License"
										value={devkit?.apollocore_license || devkit?.last_apollocore_license || '—'}
									/>
									<InfoRow
										label="ApolloCore License Expiration Date"
										value={formatLicenseExpiration(
											devkit?.apollocore_license_expiration || devkit?.last_apollocore_license_expiration
										)}
									/>
								</>
							)}

							<div className="pt-3 mt-3 mb-3 pb-3 border-t border-gray-200">
								<label className="block text-xs font-medium text-gray-600 mb-1">
									Notifications & Logs
								</label>

								<div className="flex flex-wrap items-center justify-around">
									<button
										onClick={toggleWatch}
										disabled={savingWatch}
										className={[
											"inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition",
											watching
											? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
											: "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
										].join(' ')}
										title={watching ? "Stop notifying me" : "Notify me when online and upload logs to S3 bucket (devkit-notification-logs)"}
										>
										{savingWatch ? (
											<span className="animate-pulse">Saving…</span>
										) : watching ? (
											<>
											<img src={bellOn} className="w-4 h-4" />
											Notifying
											</>
										) : (
											<>
											<img src={bellOff} className="w-4 h-4" />
											Notify when online
											</>
										)}
									</button>

									<button
										onClick={downloadLogs}
										disabled={devkit.status === "offline" || downloading}
										className={[
											"ml-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition disabled:opacity-50 disabled:cursor-not-allowed",
											downloading
											? "border-blue-400 text-blue-600 bg-blue-50 cursor-wait"
											: "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
										].join(" ")}
										title={
											devkit.status === "offline"
											? "Device is offline"
											: "Download /tocarocore/logs as zip"
										}
										>
										{downloading ? (
											<>
											<svg
												className="animate-spin h-4 w-4 text-blue-600"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24"
											>
												<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
												></circle>
												<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
												></path>
											</svg>
											Downloading…
											</>
										) : downloaded ? (
											<>
											<svg
												className="w-4 h-4 text-green-600"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												viewBox="0 0 24 24"
											>
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Downloaded
											</>
										) : (
											<>
											<img src={download} className="w-4 h-4" />
											Download Logs
											</>
										)}
										</button>
								</div>
							</div>

							<div className="pt-3 mt-3 border-t border-gray-200">
								<label className="block text-xs font-medium text-gray-600 mb-1">
									License Key
								</label>
								<div className="flex gap-2">
									<input
										ref={licenseInputRef}
										type="text"
										value={licenseKey}
										onChange={(e) => setLicenseKey(e.target.value)}
										onKeyDown={handleLicenseKeyPress}
										placeholder="Enter license key"
										className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										disabled={devkit.status === 'offline' || isUpdatingLicense}
									/>
									<button
										onClick={handleUpdateLicense}
										disabled={devkit.status === 'offline' || isUpdatingLicense || licenseKey.trim() === ''}
										className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
										title={devkit.status === 'offline' ? 'Device is offline' : 'Update License'}
									>
										{isUpdatingLicense ? 'Updating…' : 'Update License'}
									</button>
								</div>

								{licenseMsg && (
									<p className="text-xs mt-2 {licenseMsg.includes('Failed') ? 'text-red-600' : 'text-green-600'}">
										{licenseMsg}
									</p>
								)}
							</div>

							{canUploadModel && (
								<div className="pt-3 mt-3 border-t border-gray-200">
									<label className="block text-xs font-medium text-gray-600 mb-1">
										Models
									</label>
									<button
										type="button"
										onClick={triggerModelUpload}
										disabled={devkit.status === 'offline' || uploadingModel}
										className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
										title={devkit.status === 'offline' ? 'Device is offline' : 'Upload one or more model files to the device'}
									>
										{uploadingModel ? <SpinnerIcon /> : <UploadIcon />}
										{uploadingModel
											? modelUploadProgress
												? `Uploading ${modelUploadProgress.current}/${modelUploadProgress.total}…`
												: 'Uploading…'
											: 'Upload Models'}
									</button>
									<span className="ml-2 text-xs text-gray-500">
										Up to {MODEL_UPLOAD_BATCH_LIMIT} at a time
									</span>
									<input
										ref={modelFileInputRef}
										type="file"
										multiple
										className="hidden"
										onChange={handleModelFileChange}
									/>
									{uploadingModel && modelUploadProgress?.fileName && (
										<p className="text-xs text-gray-500 mt-2 break-words">
											Sending <span className="font-medium text-gray-700">{modelUploadProgress.fileName}</span>…
										</p>
									)}
									{modelUploadMsg && (
										<p className={`text-xs mt-2 ${modelUploadMsg.kind === 'success' ? 'text-green-700' : 'text-red-700'}`}>
											{modelUploadMsg.text}
										</p>
									)}
									{Array.isArray(modelsList) && (
										<p className="text-xs text-gray-500 mt-2 break-words">
											<span className="font-medium text-gray-600">On device: </span>
											{modelsList.length === 0 ? <em>none</em> : modelsList.join(', ')}
										</p>
									)}
									{Array.isArray(tbPackages) && tbPackages.length > 0 && (
										<div className="mt-2 space-y-1">
											{tbPackages.map((pkg) => (
												<TbPackageInfo key={pkg.filename} pkg={pkg} />
											))}
										</div>
									)}
								</div>
							)}

							{canRunSpeedTest && (
								<div className="pt-3 mt-3 border-t border-gray-200">
									<label className="block text-xs font-medium text-gray-600 mb-1">
										Network Speed Test
									</label>
									<button
										type="button"
										onClick={runSpeedTest}
										disabled={devkit.status === 'offline' || runningSpeedTest}
										className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
										title={devkit.status === 'offline' ? 'Device is offline' : 'Run a one-shot speed test against Cloudflare from the device'}
									>
										{runningSpeedTest ? <SpinnerIcon /> : null}
										{runningSpeedTest ? 'Running… (up to 90s)' : 'Run Speed Test'}
									</button>
									{speedTestError && (
										<p className="text-xs mt-2 text-red-700">{speedTestError}</p>
									)}
									{speedTestResult && !speedTestError && (
										<SpeedTestResult result={speedTestResult} />
									)}
								</div>
							)}
						</div>
						)}

					{/* Mac Mini Identity (minis have no updatable devkit apps) */}
					{isMacMini && (
						<div className='flex flex-col items-center justify-center flex-1 gap-2 py-2 opacity-20'>
							<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='currentColor' className='w-10 h-10 text-gray-500'>
								<path d='M5 3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h5v2H8a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2h-2v-2h5a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5zm0 2h14v10H5V5z'/>
							</svg>
							<span className='text-xs font-semibold uppercase tracking-widest text-gray-500'>Mac Mini</span>
						</div>
					)}

					{/* Update Actions Section */}
					{!isMacMini && (
					<div className='space-y-2 mb-4'>
						<h4 className='text-sm font-semibold text-gray-900 flex items-center gap-2'>
							<span className='w-1 h-4 bg-blue-500 rounded-full'></span>
							System Updates
						</h4>

						<div className='rounded-lg border border-blue-100 bg-blue-50 divide-y divide-blue-100 overflow-hidden'>
							{[
								...(hasApolloCore ? [{ key: 'apollocore', name: 'ApolloCore', color: '#036AA9', version: apolloCoreVersion, build: apolloCoreBuild, updateAvailable: apolloCoreHasUpdates }] : []),
								{ key: 'proteuscore', name: 'ProteusCore', color: '#036AA9', version: proteusCoreVersion, build: proteusCoreBuild, updateAvailable: proteusCoreHasUpdates },
								{ key: 'ironman', name: 'Configuration App', color: '#003759', version: ironmanVersion, build: ironmanBuild, updateAvailable: ironmanHasUpdates },
								...(admin ? [{ key: 'poseidon', name: 'Poseidon', color: '#57585B', version: poseidonVersion || 'unknown', build: '', updateAvailable: false }] : []),
							].map((app) => (
								<button
									key={app.key}
									type='button'
									onClick={() => openUpdateModal(app.key)}
									disabled={devkit.status === 'offline'}
									title={devkit.status === 'offline' ? 'Device is offline' : `Update ${app.name}`}
									className='w-full flex items-center justify-between px-3 py-1.5 text-left hover:bg-blue-100/70 transition-colors disabled:cursor-not-allowed'
								>
									<span className='text-xs font-medium text-gray-900 flex items-center gap-2'>
										<span className='w-2 h-2 rounded-full' style={{ backgroundColor: app.color }} />
										{app.name}
										{app.updateAvailable && (
											<span className='w-2 h-2 rounded-full bg-red-500' title='Update available' />
										)}
									</span>
									<span className='flex items-center gap-1.5'>
										<span className='text-xs text-gray-900 font-medium'>
											{app.version !== 'unknown'
												? `v${app.version}${app.build ? ` (${app.build})` : ''}`
												: 'Version unknown'}
										</span>
										<span className='text-xs text-gray-400'>›</span>
									</span>
								</button>
							))}
						</div>
					</div>
					)}

					{/* Remote Access Section */}
					<div className='space-y-2 mt-auto'>
						<h4 className='text-sm font-semibold text-gray-900 flex items-center gap-2'>
							<span className='w-1 h-4 bg-green-500 rounded-full'></span>
							Remote Access
							<ConnectionCount connections={connections} className='ml-auto' />
						</h4>
						{/* 2-col grid: buttons pair up per row; when the count is odd
						    the last button spans the full width. */}
						<div className='grid grid-cols-2 gap-2'>
							{admin && (
								<RemoteButton
									onClick={() => openSSH(devkit.tailscale.ipv4[0])}
									disabled={!remoteOnline}
									title={isMacMini && !remoteOnline ? 'SSH port (22) not reachable from the dashboard backend' : undefined}
								>
									{isMacMini ? <img src={appleIconWhite} alt="Apple" className="w-4 h-4" /> : <TerminalIcon />}
									SSH Terminal
								</RemoteButton>
							)}
							<RemoteButton
								onClick={openVncSelector}
								disabled={!remoteOnline}
								title={isMacMini && !remoteOnline ? 'VNC port (5900) not reachable from the dashboard backend' : undefined}
								// full width when it's the only button
								className={!admin && !kvmHost && !sunshineInstalled ? 'col-span-2' : ''}
							>
								{isMacMini ? <img src={appleIconWhite} alt="Apple" className="w-4 h-4" /> : <DesktopIcon />}
								VNC Desktop
							</RemoteButton>
							{kvmHost && (
								<RemoteButton
									onClick={() => setKvmOpen(true)}
									disabled={!kvmOnline}
									className={admin && !sunshineInstalled ? 'col-span-2' : ''}
									title={kvmOnline ? 'JetKVM Console' : 'JetKVM not reachable from the dashboard backend'}
								>
									<KVMIcon />
									KVM
								</RemoteButton>
							)}
							{/* Moonlight Stream only for devices with Sunshine installed
							    (flag from Back4App). Install is done from the
							    Sunshine Streaming menu. */}
							{sunshineInstalled && (
								<RemoteButton
									onClick={() => setStreamOpen(true)}
									disabled={!remoteOnline}
									// spans full width when the buttons before it fill complete rows
									className={admin !== Boolean(kvmHost) ? 'col-span-2' : ''}
								>
									<StreamIcon />
									Moonlight Stream
								</RemoteButton>
							)}
						</div>
					</div>
					</div>
				</div>
			</div>

			{/* Modals and Overlays */}
			{terminalOpen && sshHost ? (
				<SSHTerminal
					host={sshHost}
					onClose={() => setTerminalOpen(false)}
					imageVersion={devkit.last_image_version}
					macMini={isMacMini}
					sshUsername={isMacMini ? devkit.vnc_username ?? undefined : undefined}
					sshPassword={isMacMini ? devkit.vnc_password ?? undefined : undefined}
				/>
			) : null}

			{showVncSelector && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
					<div className='bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4'>
						<h3 className='text-lg font-semibold mb-4 text-gray-900'>
							Select VNC Connection Mode
						</h3>
						<div className='space-y-3'>
							<button
								onClick={() => handleVncChoice('direct')}
								className='w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium'
							>
								<DirectIcon />
								Direct VNC Connection
							</button>
							<button
								onClick={() => handleVncChoice('proxy')}
								className='w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 font-medium'
							>
								<ProxyIcon />
								VNC via Proxy
							</button>
						</div>
						<button
							onClick={() => setShowVncSelector(false)}
							className='mt-4 text-sm text-gray-500 hover:text-gray-700 underline w-full text-center'
						>
							Cancel
						</button>
					</div>
				</div>
			)}

			{vncMode === 'direct' && (
				<VNCViewer
					url={`ws://${devkit.tailscale.ipv4[0]}:5900`}
					host={devkit.tailscale.ipv4[0]}
					onClose={closeVnc}
					title='Direct VNC'
					deviceOnline={remoteOnline}
					deviceName={`Devkit ${devkit.devkit_id}`}
				/>
			)}

			{vncMode === 'proxy' && (
				<VNCProxy
					host={devkit.tailscale.ipv4[0]}
					port={5900}
					macMini={isMacMini}
					vncUsername={isMacMini ? devkit.vnc_username : undefined}
					vncPassword={isMacMini ? devkit.vnc_password : undefined}
					onClose={closeVnc}
					deviceOnline={remoteOnline}
					deviceName={`Devkit ${devkit.devkit_id}`}
				/>
			)}

			{kvmOpen && kvmHost ? (
				<KVMView
					title={`Devkit ${devkit.devkit_id} KVM`}
					onClose={() => setKvmOpen(false)}
				/>
			) : null}

			{streamOpen && (
				<SunshineView
					devkit={devkit}
					admin={admin}
					onClose={() => setStreamOpen(false)}
					deviceOnline={remoteOnline}
				/>
			)}

			{updateModalApp && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4'>
					<div className='bg-white w-full max-w-md rounded-2xl shadow-2xl p-5'>
						<div className='flex items-center justify-between pb-3 mb-4 border-b border-gray-200'>
							<h3 className='text-lg font-semibold text-gray-900'>
								{devkit.tailscale.name.slice(0, 4)} — System Update
							</h3>
							<button
								type='button'
								onClick={closeUpdateModal}
								disabled={busyUpdateApps.has(updateModalApp)}
								title={busyUpdateApps.has(updateModalApp) ? 'Update in progress' : 'Close'}
								className='p-1 text-gray-400 hover:text-gray-700 leading-none disabled:opacity-40 disabled:cursor-not-allowed'
							>
								✕
							</button>
						</div>
						{updateModalApp === 'apollocore' && (
							<ActionButton
								host={devkit.tailscale.ipv4[0]}
								variant='primary'
								app='apollocore'
								appDisplayName='ApolloCore'
								updatingApp={updatingApolloCore}
								onClick={() => setUpdatingApolloCore((p) => !p)}
								disabled={devkit.status === 'offline'}
								version={apolloCoreVersion}
								build={apolloCoreBuild}
								setRefreshDevkit={setRefreshDevkit}
								updateAvailable={apolloCoreHasUpdates}
								releaseChannel={apolloCoreUpdateChannel}
								setReleaseChannel={setApolloCoreUpdateChannel}
								poseidonVersion={poseidonVersion}
								onSendingChange={handleUpdateActiveChange('apollocore')}
								inlinePanel
							>
								Update
							</ActionButton>
						)}
						{updateModalApp === 'proteuscore' && (
							<ActionButton
								host={devkit.tailscale.ipv4[0]}
								variant='primary'
								app='proteuscore'
								appDisplayName='ProteusCore'
								updatingApp={updatingProteusCore}
								onClick={() => setUpdatingProteusCore((p) => !p)}
								disabled={devkit.status === 'offline'}
								version={proteusCoreVersion}
								build={proteusCoreBuild}
								setRefreshDevkit={setRefreshDevkit}
								updateAvailable={proteusCoreHasUpdates}
								releaseChannel={proteusCoreUpdateChannel}
								setReleaseChannel={setProteusCoreUpdateChannel}
								poseidonVersion={poseidonVersion}
								onSendingChange={handleUpdateActiveChange('proteuscore')}
								inlinePanel
							>
								Update
							</ActionButton>
						)}
						{updateModalApp === 'ironman' && (
							<ActionButton
								host={devkit.tailscale.ipv4[0]}
								variant='dark'
								app='ironman'
								appDisplayName='Configuration App'
								updatingApp={updatingIronman}
								onClick={() => setUpdatingIronman((p) => !p)}
								disabled={devkit.status === 'offline'}
								version={ironmanVersion}
								build={ironmanBuild}
								setRefreshDevkit={setRefreshDevkit}
								updateAvailable={ironmanHasUpdates}
								releaseChannel={ironmanUpdateChannel}
								setReleaseChannel={setIronmanUpdateChannel}
								onSendingChange={handleUpdateActiveChange('ironman')}
								inlinePanel
							>
								Update
							</ActionButton>
						)}
					</div>
				</div>
			)}

			{confirmPoseidon && (
				<Modal>
					<div className='w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4'>
						<UpdateIcon className='w-8 h-8 text-orange-600' />
					</div>
					<h3 className='text-lg font-semibold text-gray-900'>
						Confirm Poseidon Update
					</h3>
					<p className='text-sm text-gray-600 mt-2'>
						This will update <strong>Poseidon</strong> to the latest version.
						The device may be temporarily unavailable during the update process.
					</p>
					<ModalActions
						onConfirm={() => {
							setConfirmPoseidon(false);
							performPoseidonUpdate();
						}}
						onCancel={() => setConfirmPoseidon(false)}
					/>
				</Modal>
			)}

			{confirmationPopup && (
				<Modal>
					<div className={`w-16 h-16 ${confirmationPopup.ok ? 'bg-green-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
						{confirmationPopup.ok ? (
							<SuccessIcon className='w-8 h-8 text-green-600' />
						) : (
							<ErrorIcon className='w-8 h-8 text-red-600' />
						)}
					</div>
					<h3 className='text-lg font-semibold text-gray-900'>
						{confirmationPopup.ok ? 'Update Request Sent' : 'Update Failed'}
					</h3>
					<p className='text-sm text-gray-600 mt-2 break-words'>
						{confirmationPopup.ok
							? 'Poseidon accepted the update. It will restart to apply it and the version shown here will refresh shortly.'
							: confirmationPopup.message}
					</p>
					<button
						onClick={() => setConfirmationPopup(null)}
						className={`mt-4 w-full px-4 py-2 rounded-lg text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmationPopup.ok ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'}`}
					>
						Done
					</button>
				</Modal>
			)}
		</>
	);
};

export default DevkitCard;

// Helper Components

// Compact contents of one .tbpackage (GET /models/tbpackage). Weight fields are
// read defensively since an older/newer schema may omit or reshape them.
const TbPackageInfo = ({ pkg }) => {
	const cameras = Array.isArray(pkg?.camera_models) ? pkg.camera_models : [];
	const radars = Array.isArray(pkg?.radar_models) ? pkg.radar_models : [];

	const lensVersions = (lenses) =>
		Object.entries(lenses || {})
			.map(([lens, weights]) => {
				const versions = Object.values(weights || {})
					.map((w) => w?.version)
					.filter(Boolean);
				return versions.length ? `${lens.toUpperCase()}: ${versions.join(', ')}` : null;
			})
			.filter(Boolean);

	return (
		<details className="text-xs text-gray-500">
			<summary className="cursor-pointer break-all">
				<span className="font-medium text-gray-600">{pkg?.filename}</span>
			</summary>
			<div className="pl-3 mt-1 space-y-1">
				{cameras.map((cam) => (
					<div key={`cam-${cam.id ?? cam.name}`} className="break-words">
						<span className="text-gray-600">{cam.name}</span>
						{lensVersions(cam.lenses).map((line) => (
							<div key={line} className="pl-3">{line}</div>
						))}
					</div>
				))}
				{radars.map((rdr) => {
					const versions = Object.values(rdr.weights || {})
						.map((w) => w?.version)
						.filter(Boolean);
					return (
						<div key={`rdr-${rdr.id ?? rdr.name}`} className="break-words">
							<span className="text-gray-600">{rdr.name}</span>
							{versions.length > 0 && <span className="pl-2">{versions.join(', ')}</span>}
						</div>
					);
				})}
				{cameras.length === 0 && radars.length === 0 && <div>No models</div>}
			</div>
		</details>
	);
};

const InfoRow = ({ label, value, mono = false }) => (
	<div className='flex items-start justify-between gap-3 py-1'>
		<span className='text-xs text-gray-600'>{label}</span>
		<span
			className={`text-xs text-right ${mono
				? 'font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-800'
				: 'text-gray-900 font-medium'
				}`}
		>
			{value}
		</span>
	</div>
);

const RemoteButton = ({ children, className = '', ...props }) => (
	<button
		{...props}
		className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-600 hover:bg-gray-900 text-white focus:ring-gray-600 ${className}`}
	>
		{children}
	</button>
);

// Icon Components
const SpinnerIcon = () => (
	<svg
		className='animate-spin h-4 w-4'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<circle
			className='opacity-25'
			cx='12'
			cy='12'
			r='10'
			strokeLinecap='round'
		/>
		<path className='opacity-75' d='M4 12a8 8 0 018-8' strokeLinecap='round' />
	</svg>
);

const SuccessIcon = ({ className = 'h-12 w-12' }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
		/>
	</svg>
);

const ErrorIcon = ({ className = 'h-12 w-12' }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
		/>
	</svg>
);

const UpdateIcon = ({ className = 'h-12 w-12' }) => (
	<svg
		className={className}
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'
		/>
	</svg>
);

const TerminalIcon = () => (
	<svg
		className='w-4 h-4'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
		/>
	</svg>
);

const DesktopIcon = () => (
	<svg
		className='w-4 h-4'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
		/>
	</svg>
);

const KVMIcon = () => (
	<svg className='w-4 h-4' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2}>
		<rect x='2' y='7' width='20' height='12' rx='2' />
		<path strokeLinecap='round' d='M6 11h.01M10 11h.01M14 11h.01M18 11h.01M7 15h10' />
	</svg>
);

const StreamIcon = () => (
	<svg
		className='w-4 h-4'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2zM10 7.5l4.5 2.5-4.5 2.5v-5zM9 21h6'
		/>
	</svg>
);

const UploadIcon = () => (
	<svg
		className='w-4 h-4'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12'
		/>
	</svg>
);

const DirectIcon = () => (
	<svg
		className='w-5 h-5'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M13 10V3L4 14h7v7l9-11h-7z'
		/>
	</svg>
);

const ProxyIcon = () => (
	<svg
		className='w-5 h-5'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth={2}
	>
		<path
			strokeLinecap='round'
			strokeLinejoin='round'
			d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
		/>
	</svg>
);

const Modal = ({ children }) => (
	<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4'>
		<div className='bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 text-center space-y-4'>
			{children}
		</div>
	</div>
);

const ModalActions = ({ onConfirm, onCancel }) => (
	<div className='flex gap-3 mt-6'>
		<button
			onClick={onCancel}
			className='flex-1 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300'
		>
			Cancel
		</button>
		<button
			onClick={onConfirm}
			className='flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500'
		>
			Confirm
		</button>
	</div>
);
